<template>
  <div class="audio-player">
    <audio ref="audioElement" :src="audio.audio_url" preload="auto" @loadedmetadata="updateAudioDuration" @ended="handleAudioEnded" @timeupdate="updateProgress"></audio>

    <video ref="videoElement" @ended="handleAudioEnded" @timeupdate="updateProgress" class="hidden-video" width="100%" height="193px" controls style="border-radius: 5px; background-color: #000;">
      <source :src="audio.audio_url" type="video/mp4">
    </video>

    <button @click="togglePlayPause" class="play-pause-btn">
      <v-icon v-if="!isPlaying">mdi-play</v-icon>
      <v-icon v-else>mdi-pause</v-icon>
    </button>

    <div class="audio-content">
      <div class="audio-visualization">
        <div class="progress-dot" :style="{ left: progressPosition + '%' }"></div>
      </div>
      <div class="audio-info">
        <span class="duration">{{ audio.duration }}</span>
        <span class="date">{{ formatDateTime() }}</span>
      </div>
    </div>

    <div class="user-initial" @click="open">{{ audio.created_by_user.name.charAt(0).toUpperCase() }}</div>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  emits: ['open'],
  props: {
    audio: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isPlaying: false,
      progressPosition: 0,
      playRequested: false,
    };
  },
  mounted() {
    this.$refs.audioElement.addEventListener('canplaythrough', this.handleCanPlayThrough);
  },
  beforeDestroy() {
    this.$refs.audioElement.removeEventListener('canplaythrough', this.handleCanPlayThrough);
  },
  methods: {
    togglePlayPause() {
      const mediaElement = this.isIphone() ? this.$refs.videoElement : this.$refs.audioElement;

      if (this.isPlaying) {
        mediaElement.pause();
      } else {
        mediaElement.play();
      }
      this.isPlaying = !this.isPlaying;
    },
    handleCanPlayThrough() {
      if (this.playRequested && !this.isPlaying) {
        this.playOrPause();
      }
    },
    handleAudioEnded() {
      this.isPlaying = false;
      this.progressPosition = 0;
    },
    formatDuration(duration) {
      if (!Number.isFinite(duration)) {
        return "00:00";
      }

      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    updateProgress() {
      const mediaElement = this.isIphone() ? this.$refs.videoElement : this.$refs.audioElement;
      const {currentTime} = mediaElement;
      const {duration} = mediaElement;
      const progress = (currentTime / duration) * 100;
      this.progressPosition = progress;
    },
    formatDateTime() {
      const date = new Date(this.audio.created_at);

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      }

      if (date.toDateString() === yesterday.toDateString()) {
        return `Ontem às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
      }

      if (date.getFullYear() === today.getFullYear()) {
        return date.toLocaleDateString('pt-BR', { month: 'long', day: 'numeric' }) + ' às ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      }

      return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }) + ' às ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    updateAudioDuration() {
      if (this.$refs.audioElement.duration === Infinity) {
        this.$refs.audioElement.currentTime = Number.MAX_SAFE_INTEGER;
        this.$refs.audioElement.ontimeupdate = () => {
          this.$refs.audioElement.ontimeupdate = null;
          this.$refs.audioElement.currentTime = 0;
          this.audioDuration = this.formatDuration(this.$refs.audioElement.duration);
        };
      } else {
        this.audioDuration = this.formatDuration(this.$refs.audioElement.duration);
      }
    },
    open() {
      this.$emit('open', this.audio);
    },
    isIphone() {
      if (this.getOS() === 'iOS') {
        return true;
      }
      return !!(this.$route.query.t && this.$route.query.t === 'ios');
    },
    getOS() {
      const uA = navigator.userAgent || navigator.vendor || window.opera;
      if ((/iPad|iPhone|iPod/.test(uA) && !window.MSStream) || (uA.includes('Mac') && 'ontouchend' in document)) return 'iOS';
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.audio-player {
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 6px;
  padding: 20px 16px;
  gap: 20px;
  height: 60px;
}

.audio-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: 10px;
  margin-top: 15px;
}

.audio-visualization {
  position: relative;
  width: 100%;
  height: 2px;
  background-color: #f2f2f2;
  border-radius: 10px;
}

.progress-dot {
  transition: left 0.5s linear;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  background-color: #48b38b;
  border-radius: 50%;
}

.audio-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
}

.user-initial {
  width: 64.1px !important;
  height: 42px;
  background-color: #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-weight: bold;
}

.hidden-video {
  display: none;
}
</style>
